import React, { useState } from 'react';
import { Ticket, Bed, Music, Star, Utensils } from 'lucide-react';
import { mockData } from '../mock';
import { useT } from '../context/LanguageContext';
import { useSignup, DEFAULT_SIGNUP } from '../context/SignupContext';
import { useContent, DEFAULT_PRICING } from '../context/ContentContext';
import { EditButton } from './admin/EditorShell';
import { PricingEditor } from './admin/PricingEditor';
import { SchemaEditor } from './admin/SchemaEditor';

const ICONS = { ticket: Ticket, bed: Bed, music: Music, star: Star, utensils: Utensils };

const PriceCard = ({ icon, title, price, desc, testId }) => {
  const Icon = ICONS[icon] || Star;
  return (
    <div className="rounded-3xl bg-gradient-to-b from-[#1a1424] to-[#0f0a17] border border-white/5 p-8 hover:border-[#ff2e2e]/50 transition-colors" data-testid={testId}>
      <div className="w-14 h-14 rounded-2xl bg-[#ff2e2e]/10 flex items-center justify-center text-[#ff2e2e] mb-6">
        <Icon size={28} strokeWidth={2} />
      </div>
      <h3 className="text-white text-xl font-bold mb-2">{title}</h3>
      <p className="text-4xl font-black text-[#ff2e2e] mb-4" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{price}</p>
      <p className="text-white/60 text-sm leading-relaxed">{desc}</p>
    </div>
  );
};

const CTA = () => {
  const { t } = useT();
  const { openSignup } = useSignup();
  const { content } = useContent();
  const [editing, setEditing] = useState(false);
  const [editingSignup, setEditingSignup] = useState(false);
  const p = { ...DEFAULT_PRICING, ...(content.pricing || {}) };
  const SIGNUP_SCHEMA = [
    { key: 'mode', label: 'Kva skjer når ein trykkjer «Meld deg på»?', type: 'select', options: [['popup', 'Vis popup-melding'], ['link', 'Send vidare til ei lenke (påmeldingsskjema)']] },
    { key: 'url', label: 'Lenke til påmeldingsskjema (brukt når «Send vidare» er valt)', type: 'url' },
    { key: 'title', label: 'Popup: overskrift' },
    { key: 'text', label: 'Popup: tekst', type: 'textarea', rows: 3 },
    { key: 'button', label: 'Popup: knappetekst' },
    { key: 'buttonUrl', label: 'Popup: knappelenke (t.d. /#nyheitsbrev eller https://…)' },
  ];
  const cols = p.cards.length >= 3 ? 'md:grid-cols-3' : p.cards.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'max-w-md mx-auto';

  return (
    <section id="pamelding" className="relative bg-black py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${mockData.ctaImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />

      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="text-center mb-14 relative">
          <div className="absolute right-0 top-0 flex flex-col sm:flex-row gap-2"><EditButton onClick={() => setEditing(true)} testId="edit-pricing-button" /><EditButton onClick={() => setEditingSignup(true)} label="Påmeldingsknapp" testId="edit-signup-button" /></div>
          {p.kicker && <p className="text-[#ff2e2e] font-semibold uppercase tracking-widest mb-3" data-testid="pricing-kicker">{t(p.kicker)}</p>}
          <h2 className="text-5xl md:text-7xl font-black text-white tracking-tighter" style={{ fontFamily: "'Oktah Round', sans-serif" }} data-testid="pricing-heading">
            {t(p.heading)}
          </h2>
          {p.intro && <p className="text-white/70 max-w-2xl mx-auto mt-4 text-lg">{t(p.intro)}</p>}
        </div>

        {p.cards.length > 0 && (
          <div className={`grid grid-cols-1 ${cols} gap-6 mb-12`} data-testid="pricing-cards">
            {p.cards.map((c, i) => <PriceCard key={c.id || i} icon={c.icon} title={t(c.title)} price={c.price} desc={t(c.desc)} testId={`pricing-card-${i}`} />)}
          </div>
        )}

        {p.note && <p className="text-center text-white/60 max-w-3xl mx-auto mb-10 text-sm md:text-base">{t(p.note)}</p>}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button type="button" onClick={openSignup} data-testid="cta-signup-link" className="px-12 py-4 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide text-center hover:bg-[#ff4d4d] transition-colors shadow-lg shadow-[#ff2e2e]/30">
            {t(p.primary)}
          </button>
          {p.secondary && (
            <button type="button" onClick={openSignup} data-testid="cta-concert-link" className="px-12 py-4 rounded-full border-2 border-[#ff2e2e] text-[#ff2e2e] font-bold uppercase tracking-wide text-center hover:bg-[#ff2e2e] hover:text-black transition-colors">
              {t(p.secondary)}
            </button>
          )}
        </div>
      </div>
      {editing && <PricingEditor initial={p} onClose={() => setEditing(false)} />}
      {editingSignup && <SchemaEditor contentKey="signup" title="Rediger påmeldingsknappane" single initial={{ ...DEFAULT_SIGNUP, ...(content.signup || {}) }} schema={SIGNUP_SCHEMA} onClose={() => setEditingSignup(false)} />}
    </section>
  );
};

export default CTA;
